import { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { usePlatform } from "@/context/PlatformContext";
import { JasrMark } from "@/components/JasrLogo";
import { Loader2, ArrowLeft, Zap, ShieldCheck, RefreshCw } from "lucide-react";

const features = [
  { icon: Zap, text: "مزامنة فورية بين قيود وسلة" },
  { icon: ShieldCheck, text: "بيانات محمية ومشفّرة بالكامل" },
  { icon: RefreshCw, text: "تحديث تلقائي كل 15 دقيقة" },
];

export default function Login() {
  const { user, login, error } = useAuth();
  const { name, tagline, logo_base64 } = usePlatform();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  if (user === null) return <FullscreenLoader />;
  if (user) return <Navigate to="/dashboard" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    await login(email, password);
    setBusy(false);
  };

  const LogoIcon = () => logo_base64
    ? <img src={logo_base64} alt={name} className="w-6 h-6 object-contain" />
    : <JasrMark size={24} />;

  return (
    <div className="min-h-screen flex" dir="rtl">
      {/* ── Left visual panel (RTL = right side visually) ── */}
      <div className="hidden lg:flex w-[52%] relative flex-col overflow-hidden bg-[#060810]">
        {/* Background */}
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1694875522449-e852b63be76d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600')",
            filter: "brightness(0.22) saturate(1.2)",
          }}
        />
        {/* Blue gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#004EEB]/30 via-transparent to-[#6366F1]/20" />
        {/* Dot grid */}
        <div className="absolute inset-0 dot-grid opacity-30" />
        <div className="grain absolute inset-0" />

        {/* Glowing orbs */}
        <div className="absolute top-1/4 left-1/3 w-64 h-64 bg-[#004EEB]/15 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-48 h-48 bg-[#6366F1]/15 rounded-full blur-3xl" />

        {/* Content */}
        <div className="relative z-10 flex flex-col h-full p-12">
          {/* Brand */}
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#004EEB] rounded-xl grid place-items-center glow-blue">
              <LogoIcon />
            </div>
            <div>
              <div className="text-white font-black text-base tracking-tight" style={{ fontFamily: "'Tajawal', sans-serif" }}>
                {name}<span className="text-[#4d84ff]">.</span>
              </div>
              <div className="text-white/40 text-[10px] tracking-widest uppercase">{tagline}</div>
            </div>
          </div>

          {/* Main copy */}
          <div className="flex-1 flex flex-col justify-center space-y-6 fade-up">
            <div>
              <span className="inline-block text-[11px] tracking-[0.2em] uppercase text-[#4d84ff]/80 font-semibold border border-[#004EEB]/30 bg-[#004EEB]/10 px-3 py-1 rounded-full mb-4">
                Multi-Platform · متعدد المنصات
              </span>
              <h1 className="text-4xl xl:text-5xl font-bold text-white leading-tight">
                اربط متجرك<br />
                <span className="text-transparent bg-clip-text bg-gradient-to-l from-[#4d84ff] to-[#818cf8]">
                  بكل الأنظمة
                </span>
              </h1>
              <p className="text-white/50 mt-4 max-w-md leading-relaxed text-[15px]">
                منصة وسيطة ذكية تربط أنظمة المحاسبة بمتاجر التجارة الإلكترونية وتوحّد المخزون من جميع الفروع تلقائياً.
              </p>
            </div>

            {/* Feature pills */}
            <div className="space-y-2.5">
              {features.map(({ icon: Icon, text }) => (
                <div key={text} className="flex items-center gap-3 text-white/60 text-sm">
                  <div className="w-7 h-7 bg-white/5 border border-white/10 rounded-lg grid place-items-center shrink-0">
                    <Icon className="w-3.5 h-3.5 text-[#4d84ff]" />
                  </div>
                  {text}
                </div>
              ))}
            </div>
          </div>

          {/* Bottom stats */}
          <div className="flex gap-8 text-white/40 text-xs border-t border-white/10 pt-6">
            <div>
              <div className="text-white text-xl font-bold">99.8%</div>
              <div>نجاح التزامن</div>
            </div>
            <div>
              <div className="text-white text-xl font-bold">&lt;15s</div>
              <div>متوسط الاستجابة</div>
            </div>
            <div>
              <div className="text-white text-xl font-bold">∞</div>
              <div>عدد الفروع</div>
            </div>
          </div>
          <p className="text-white/25 text-xs mt-4">
            ليس لديك حساب؟{" "}
            <Link to="/register" className="text-[#60A5FA] hover:text-white transition-colors">أنشئ حساباً مجانياً</Link>
          </p>
        </div>
      </div>

      {/* ── Right form panel ── */}
      <div className="flex-1 flex items-center justify-center bg-[#F8FAFC] p-6 lg:p-12">
        <form onSubmit={onSubmit} className="w-full max-w-md space-y-7 fade-up">
        {/* Mobile brand */}
          <div className="lg:hidden flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 bg-[#004EEB] rounded-lg grid place-items-center">
                <LogoIcon />
              </div>
              <span className="font-black text-slate-900" style={{ fontFamily: "'Tajawal', sans-serif" }}>
                {name}<span className="text-[#004EEB]">.</span>
              </span>
            </div>
            <Link to="/register" className="text-sm text-[#004EEB] font-semibold hover:underline">إنشاء حساب</Link>
          </div>

          <div>
            <p className="text-[11px] tracking-[0.18em] uppercase text-slate-400 font-semibold">
              تسجيل الدخول
            </p>
            <h2 className="text-3xl font-bold mt-1.5 tracking-tight text-slate-900">أهلاً بعودتك</h2>
            <p className="text-slate-500 mt-2 text-sm">
              سجّل الدخول للوصول إلى لوحة تحكم المخزون الموحد.
            </p>
          </div>

          <div className="space-y-4">
            <label className="block space-y-1.5">
              <span className="text-[11px] tracking-[0.1em] uppercase text-slate-500 font-semibold">
                البريد الإلكتروني
              </span>
              <input
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-white border border-slate-200 px-4 py-3 rounded-xl text-sm focus:outline-none focus:border-[#004EEB] focus:ring-2 focus:ring-[#004EEB]/15 transition-all text-right shadow-sm"
                required
              />
            </label>
            <label className="block space-y-1.5">
              <span className="text-[11px] tracking-[0.1em] uppercase text-slate-500 font-semibold">
                كلمة المرور
              </span>
              <input
                data-testid="login-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-white border border-slate-200 px-4 py-3 rounded-xl text-sm focus:outline-none focus:border-[#004EEB] focus:ring-2 focus:ring-[#004EEB]/15 transition-all text-right shadow-sm"
                required
              />
            </label>
          </div>

          {error && (
            <div
              data-testid="login-error"
              className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-4 py-3"
            >
              {error}
            </div>
          )}

          <button
            data-testid="login-submit-button"
            type="submit"
            disabled={busy}
            className="w-full bg-[#004EEB] hover:bg-[#0040C1] disabled:opacity-60 text-white font-semibold py-3 rounded-xl transition-all duration-200 flex items-center justify-center gap-2 shadow-lg shadow-[#004EEB]/20 hover:shadow-[#004EEB]/30 hover:-translate-y-0.5"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            <span>دخول</span>
            <ArrowLeft className="w-4 h-4 rtl-flip" />
          </button>

          <div className="text-xs text-slate-500 flex items-center justify-center gap-2 pt-1">
            ليس لديك حساب؟{" "}
            <Link to="/register" className="text-[#004EEB] font-semibold hover:underline">
              إنشاء حساب جديد
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}

function FullscreenLoader() {
  return (
    <div className="min-h-screen grid place-items-center bg-[#F8FAFC]">
      <div className="flex flex-col items-center gap-3">
        <div className="w-10 h-10 bg-[#004EEB] rounded-xl grid place-items-center glow-blue">
          <Loader2 className="w-5 h-5 text-white animate-spin" />
        </div>
        <p className="text-slate-400 text-sm">جارٍ التحميل...</p>
      </div>
    </div>
  );
}
